/**
 * AutoSEOWebhookDiagnostics
 *
 * Phase 2L-A71 — Admin-only panel embedded inside /admin/blog that
 * surfaces the GetAutoSEO / AutoSEO webhook health + last 10 webhook
 * attempts. Uses the frontend's normal JWT (via `api` axios wrapper)
 * so the admin can read prod diagnostics from the UI without needing
 * shell / curl access.
 *
 * Backend endpoints (already Admin-protected):
 *   GET /api/admin/blog/_health
 *   GET /api/admin/blog/webhook-log?limit=10
 *
 * Does NOT expose the raw webhook secret — the `_health` payload
 * already redacts it to `xxxx…yyyy (len=N)`.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-AU", {
      dateStyle: "medium",
      timeStyle: "medium",
    });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const map = {
    200: "bg-emerald-100 text-emerald-700",
    400: "bg-rose-100 text-rose-700",
    401: "bg-amber-100 text-amber-700",
    403: "bg-amber-100 text-amber-700",
    500: "bg-rose-100 text-rose-700",
  };
  const cls = map[status] || "bg-slate-100 text-slate-700";
  return (
    <span
      data-testid="autoseo-diag-status-badge"
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold tabular-nums ${cls}`}
    >
      {status ?? "—"}
    </span>
  );
}

function KV({ label, value, mono = false, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-slate-100 last:border-b-0">
      <dt className="text-xs text-slate-500 uppercase tracking-wide">{label}</dt>
      <dd
        data-testid={testId}
        className={`text-sm text-slate-900 ${mono ? "font-mono break-all text-right" : "text-right"}`}
      >
        {value ?? "—"}
      </dd>
    </div>
  );
}

export default function AutoSEOWebhookDiagnostics() {
  const [open, setOpen] = useState(true);
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [h, l] = await Promise.all([
        api.get("/admin/blog/_health"),
        api.get("/admin/blog/webhook-log?limit=10"),
      ]);
      setHealth(h.data);
      setLogs(l.data?.items || []);
    } catch (e) {
      const code = e?.response?.status || "error";
      const detail = e?.response?.data?.detail;
      setErr(`HTTP ${code}${detail ? `: ${detail}` : ""}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const latest = logs && logs.length ? logs[0] : null;
  const latestMeta = latest?.meta || {};

  return (
    <section
      data-testid="autoseo-webhook-diagnostics"
      className="max-w-6xl mx-auto mb-6 rounded-xl ring-1 ring-slate-200 bg-white shadow-sm overflow-hidden"
    >
      <header className="px-4 py-3 flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-slate-800 tracking-tight">
            AutoSEO Webhook Diagnostics
          </h2>
          {health?.secret_configured ? (
            <span
              data-testid="autoseo-diag-secret-ok"
              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium"
            >
              SECRET SET
            </span>
          ) : (
            <span
              data-testid="autoseo-diag-secret-missing"
              className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-medium"
            >
              SECRET MISSING
            </span>
          )}
          {loading && (
            <span className="text-[11px] text-slate-500">loading…</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            data-testid="autoseo-diag-refresh"
            className="text-xs px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-800 transition"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="autoseo-diag-toggle"
            className="text-xs px-2 py-1 rounded ring-1 ring-slate-300 text-slate-700 hover:bg-slate-100 transition"
          >
            {open ? "Hide" : "Show"}
          </button>
        </div>
      </header>

      {err && (
        <p data-testid="autoseo-diag-error" className="px-4 py-2 text-xs text-rose-700 bg-rose-50">
          {err}
        </p>
      )}

      {open && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
          {/* ─── Health ─── */}
          <div className="rounded-lg ring-1 ring-slate-100 p-3 bg-slate-50/40">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
              Health
            </h3>
            <dl className="space-y-0">
              <KV
                label="Secret configured"
                value={health?.secret_configured ? "yes" : "no"}
                testId="autoseo-diag-h-secret-configured"
              />
              <KV
                label="Secret length"
                value={health?.secret_length ?? "—"}
                testId="autoseo-diag-h-secret-length"
              />
              <KV
                label="Secret fingerprint"
                value={health?.secret_fingerprint || "—"}
                mono
                testId="autoseo-diag-h-secret-fingerprint"
              />
              <KV
                label="Auto-publish"
                value={health?.auto_publish ? "ON" : "OFF (drafts)"}
                testId="autoseo-diag-h-auto-publish"
              />
              <KV
                label="Total posts"
                value={health?.total ?? "—"}
                testId="autoseo-diag-h-total"
              />
              <KV
                label="Drafts"
                value={health?.draft ?? "—"}
                testId="autoseo-diag-h-draft"
              />
              <KV
                label="Published"
                value={health?.published ?? "—"}
                testId="autoseo-diag-h-published"
              />
              <KV
                label="Webhook log rows"
                value={health?.recent_log_count ?? "—"}
                testId="autoseo-diag-h-log-count"
              />
            </dl>

            {/* By-source × by-status breakdown */}
            {health?.by_source_status && (
              <div className="mt-3">
                <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Counts by source × status
                </h4>
                <div className="text-xs">
                  {Object.entries(health.by_source_status).map(([src, statuses]) => (
                    <div
                      key={src}
                      data-testid={`autoseo-diag-source-${src}`}
                      className="flex items-center justify-between py-0.5 border-b border-slate-100 last:border-b-0"
                    >
                      <span className="font-mono text-slate-600">{src}</span>
                      <span className="text-slate-700">
                        {Object.entries(statuses)
                          .map(([s, n]) => `${s}=${n}`)
                          .join(" · ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Latest post */}
            {health?.latest_post && (
              <div className="mt-3 rounded ring-1 ring-slate-100 bg-white p-2">
                <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Latest post written
                </h4>
                <KV
                  label="Title"
                  value={health.latest_post.title || "—"}
                  testId="autoseo-diag-latest-title"
                />
                <KV
                  label="Slug"
                  value={health.latest_post.slug || "—"}
                  mono
                  testId="autoseo-diag-latest-slug"
                />
                <KV
                  label="Status"
                  value={health.latest_post.status || "—"}
                  testId="autoseo-diag-latest-status"
                />
                <KV
                  label="Source"
                  value={health.latest_post.source || "—"}
                  testId="autoseo-diag-latest-source"
                />
                <KV
                  label="Modified at"
                  value={fmtDate(health.latest_post.modified_at)}
                  testId="autoseo-diag-latest-modified"
                />
              </div>
            )}
          </div>

          {/* ─── Latest webhook attempt ─── */}
          <div className="rounded-lg ring-1 ring-slate-100 p-3 bg-slate-50/40">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-2">
              Latest webhook attempt
              {latest && <StatusBadge status={latest.status} />}
            </h3>
            {!latest && (
              <p className="text-xs text-slate-500">No webhook attempts logged yet.</p>
            )}
            {latest && (
              <dl className="space-y-0">
                <KV
                  label="Received at"
                  value={fmtDate(latest.created_at)}
                  testId="autoseo-diag-latest-log-time"
                />
                <KV
                  label="Remote"
                  value={latest.remote || "—"}
                  mono
                  testId="autoseo-diag-latest-log-remote"
                />
                <KV
                  label="Detail"
                  value={latest.detail || "—"}
                  testId="autoseo-diag-latest-log-detail"
                />
                <KV
                  label="Event"
                  value={latestMeta.event || "—"}
                  testId="autoseo-diag-latest-log-event"
                />
                <KV
                  label="Article ID"
                  value={latestMeta.article_id || "—"}
                  mono
                  testId="autoseo-diag-latest-log-article-id"
                />
                <KV
                  label="Delivery"
                  value={latestMeta.delivery || "—"}
                  mono
                />
                <KV
                  label="Auth method"
                  value={latestMeta.auth_method || "—"}
                  testId="autoseo-diag-latest-log-auth-method"
                />
                <KV
                  label="Title"
                  value={latestMeta.title || "—"}
                />
                <KV
                  label="Title length"
                  value={latestMeta.title_len ?? "—"}
                />
                <KV
                  label="Slug"
                  value={latestMeta.slug || "—"}
                  mono
                />
                <KV
                  label="Has content / html / md"
                  value={`${latestMeta.has_content ? "✓" : "✗"} / ${latestMeta.has_content_html ? "✓" : "✗"} / ${latestMeta.has_content_markdown ? "✓" : "✗"}`}
                />
                <KV
                  label="Content length"
                  value={latestMeta.content_len ?? "—"}
                />
                <KV
                  label="Hero URL length"
                  value={latestMeta.hero_url_len ?? "—"}
                />
                <KV
                  label="Tags shape"
                  value={latestMeta.tags_shape || "—"}
                  mono
                />
                <KV
                  label="SEO keywords shape"
                  value={latestMeta.seo_keywords_shape || "—"}
                  mono
                />
                <KV
                  label="Received keys"
                  value={
                    Array.isArray(latestMeta.received_keys)
                      ? latestMeta.received_keys.slice(0, 30).join(", ")
                      : "—"
                  }
                  mono
                />
                {latestMeta.validation_error && (
                  <div className="mt-2 p-2 rounded bg-rose-50 ring-1 ring-rose-200">
                    <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-wide">
                      Validation error
                    </p>
                    <pre
                      data-testid="autoseo-diag-latest-log-validation-error"
                      className="text-xs text-rose-800 whitespace-pre-wrap break-words mt-1"
                    >
                      {latestMeta.validation_error}
                    </pre>
                  </div>
                )}
              </dl>
            )}
          </div>

          {/* ─── Last 10 attempts ─── */}
          <div className="lg:col-span-2 rounded-lg ring-1 ring-slate-100 overflow-hidden bg-white">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide px-3 py-2 bg-slate-50">
              Last {Math.min(logs?.length || 0, 10)} webhook attempts
            </h3>
            {!logs?.length && (
              <p className="px-3 py-3 text-xs text-slate-500">No entries.</p>
            )}
            {logs && logs.length > 0 && (
              <table
                data-testid="autoseo-diag-log-table"
                className="w-full text-xs"
              >
                <thead className="bg-slate-50 text-slate-600">
                  <tr className="text-left">
                    <th className="px-3 py-1.5 font-semibold">When</th>
                    <th className="px-3 py-1.5 font-semibold">Status</th>
                    <th className="px-3 py-1.5 font-semibold">Auth</th>
                    <th className="px-3 py-1.5 font-semibold">Event</th>
                    <th className="px-3 py-1.5 font-semibold">Article ID</th>
                    <th className="px-3 py-1.5 font-semibold">Detail / reason</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((row, idx) => (
                    <tr
                      key={`${row.created_at}-${idx}`}
                      className="border-t border-slate-100 hover:bg-slate-50/60"
                    >
                      <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">
                        {fmtDate(row.created_at)}
                      </td>
                      <td className="px-3 py-1.5">
                        <StatusBadge status={row.status} />
                      </td>
                      <td className="px-3 py-1.5 font-mono text-slate-700">
                        {row.meta?.auth_method || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-slate-700">
                        {row.meta?.event || "—"}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-slate-700 max-w-[14rem] truncate">
                        {row.meta?.article_id || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-slate-700 max-w-[28rem] truncate">
                        {row.meta?.validation_error || row.detail || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
